// ============================================================
// useHistory - undo, solo and multiplayer.
//
// Solo: a local stack of serialized snapshots, pushed after every
// tracked action. Multiplayer: a pre-action snapshot captured before
// the action, and after it re-renders the post-state is recorded onto
// the shared stack (historyShared.ts). Undo restores the STATE half of
// the roster only - a sheet edit survives it - plus the tracker.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhaseFrame } from '../engine/phase/types';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import type { TurnTrackerState, HistorySnapshot, ChargeLine } from '../types';
import type { Combatant, CombatState } from '../engine/roster/types';
import { snapshotOf } from './historySnapshots';
import { beginAction, recordSharedAction, resetSharedHistory, undoShared } from './historyShared';

interface LocalHistoryState {
  stack: string[];
  pointer: number;
  skip: boolean;
}

interface SharedHistoryState {
  canUndo: boolean;
}

interface SharedHistoryCache {
  stack: HistorySnapshot[];
  pointer: number;
}

interface SyncRefs {
  sharedHistoryState: SharedHistoryState;
  setSharedHistoryState: Dispatch<SetStateAction<SharedHistoryState>>;
  sharedHistoryCacheRef: MutableRefObject<SharedHistoryCache>;
  undoRedoBusyRef: MutableRefObject<boolean>;
  narrativeQueueRef: MutableRefObject<unknown[]>;
  narrativeFlushTimerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  /** Passed by the caller; no longer read here. */
  lastTgtJsonRef: MutableRefObject<Record<string, string>>;
  /** Passed by the caller; no longer read here. */
  tgtSyncTimerRef: MutableRefObject<Record<string, ReturnType<typeof setTimeout>>>;
}

export interface UseHistoryParams {
  sessionId: string | null;
  playerId: string | null;
  /** The roster. Undo snapshots and restores its STATE half only. */
  roster: Combatant[];
  restoreStates: (states: Record<string, CombatState>, narrativeCounts?: Record<string, number>) => void;
  activeTab: string;
  setActiveTab: Dispatch<SetStateAction<string>>;
  setPendingAttack: Dispatch<SetStateAction<Record<string, unknown> | null>>;
  isLocked: (action: string) => boolean;
  syncRefs: SyncRefs | null;
  clearAllPendingDefences: (() => void) | null;
  /**
   * Cancel the reactions this undone action created, by watermark.
   * Falls back to `clearAllPendingDefences` when absent.
   */
  clearPendingDefencesSince?: ((watermark: number | null | undefined) => void) | null;
  // Turn tracker (optional - null when turn tracker not initialized)
  turnTrackerState?: TurnTrackerState;
  setTurnTrackerState?: Dispatch<SetStateAction<TurnTrackerState>>;
  syncTurnTrackerToFirestore?: (state: TurnTrackerState) => void;
  /** What the GM is directing now - snapshotted so an undo can put it back. Null while unknown. */
  frame?: PhaseFrame | null;
  /** Put a frame back. Given only to a seat that may write it (the GM, or anyone solo). */
  imposeFrame?: ((frame: PhaseFrame) => void) | null;
  /** The kill-teams' Cohesion pools as they stand (Squad Mode), snapshotted so an undo can put a spend back. */
  cohesion?: unknown;
  /** Put the pools back (a compare-and-set). */
  imposeCohesion?: ((cohesion: unknown) => void) | null;
  /**
   * A charge's start, handed over as its strike goes (useChargeRun): the charger's token, and the run's own move
   * line (none for a hidden charger) - the next snapshot carries both, for an undo to put back and take away.
   */
  chargeOriginRef?: MutableRefObject<{ token: Record<string, unknown>; line: ChargeLine | null } | null>;
  /** Put tokens back where a snapshot says they stood (an undone charge). */
  restoreTokens?: (tokens: Record<string, unknown>[]) => void;
  /** Delete one move line from the log - locally and on the server (an undone charge's own, 2026-10-21). */
  dropLine?: (line: ChargeLine) => void;
}

export interface UseHistoryReturn {
  pushHistory: () => void;
  capturePreActionSnapshot: () => void;
  doUndo: () => Promise<void>;
  canUndo: boolean;
  /** The newest action's entry is still being written: Undo waits (a session only). */
  saving: boolean;
  historyTrack: MutableRefObject<boolean>;
  undoingRef: MutableRefObject<boolean>;
  /** Forget every snapshot, here and on the server. A scene cut's only caller. */
  resetHistory: () => void;
}

const MAX_LOCAL_HISTORY = 50;

// ── Solo (local) history ─────────────────────────────────────

/** Everything the solo stack reads and restores through. */
interface LocalContext {
  historyRef: MutableRefObject<LocalHistoryState>;
  roster: Combatant[];
  activeTab: string;
  turnTrackerState?: TurnTrackerState;
  restoreStates: UseHistoryParams['restoreStates'];
  setActiveTab: UseHistoryParams['setActiveTab'];
  setPendingAttack: UseHistoryParams['setPendingAttack'];
  setTurnTrackerState?: UseHistoryParams['setTurnTrackerState'];
  frame?: UseHistoryParams['frame'];
  imposeFrame?: UseHistoryParams['imposeFrame'];
  cohesion?: UseHistoryParams['cohesion'];
  imposeCohesion?: UseHistoryParams['imposeCohesion'];
  chargeOriginRef?: UseHistoryParams['chargeOriginRef'];
  restoreTokens?: UseHistoryParams['restoreTokens'];
  dropLine?: UseHistoryParams['dropLine'];
}

/**
 * The charge's start, taken once: the snapshot made next carries it, and no later one - the charger's token (as a
 * list, for `restoreTokens`) and the run's move line (`line`, for `dropLine`).
 */
function takeOrigin(ctx: Pick<LocalContext, 'chargeOriginRef'>): ChargeUndo | null {
  const origin = ctx.chargeOriginRef?.current;
  if (!origin || !ctx.chargeOriginRef) return null;
  ctx.chargeOriginRef.current = null;
  // Through JSON: the shared stack is a document, and a document refuses an undefined field (gotcha 2).
  const tokens = [JSON.parse(JSON.stringify(origin.token))];
  return origin.line ? { tokens, line: { ...origin.line } } : { tokens };
}

/** What an undone charge puts back and takes away: the charger's start, and its own move line. */
interface ChargeUndo {
  tokens: Record<string, unknown>[];
  line?: ChargeLine;
}

/**
 * Push the current table onto the local stack - states only, so a sheet
 * edit must survive an undo. An unchanged top is not pushed twice; a
 * redo branch is cut; the stack is capped.
 */
function pushLocal(ctx: LocalContext): void {
  const h = ctx.historyRef.current;
  const { roster } = ctx;
  try {
    const table = {
      states: Object.fromEntries(roster.map((c) => [c.id, c.state])),
      narrativeLogCounts: Object.fromEntries(roster.map((c) => [c.id, c.narrativeLog.length])),
      activeTab: ctx.activeTab,
      turnTracker: ctx.turnTrackerState,
      direction: ctx.frame ?? undefined,
      cohesion: ctx.cohesion ?? undefined,
    };
    const charge = chargeFields(takeOrigin(ctx));
    const plain = JSON.stringify(table);
    if (h.pointer >= 0 && h.stack[h.pointer] === plain) {
      // The same table, now with a charge's start: carried by the entry on top, not by a second one that would
      // make an undo that changes nothing.
      if (charge) h.stack[h.pointer] = JSON.stringify({ ...table, ...charge });
      return;
    }
    const snap = charge ? JSON.stringify({ ...table, ...charge }) : plain;
    h.stack = h.stack.slice(0, h.pointer + 1);
    h.stack.push(snap);
    if (h.stack.length > MAX_LOCAL_HISTORY) h.stack.shift();
    h.pointer = h.stack.length - 1;
  } catch (_e) {
    /* serialization too large, skip */
  }
}

/** A local entry's charge fields: the charger's start, and the run's line when it wrote one. */
const chargeFields = (c: ChargeUndo | null) => c && { tokens: c.tokens, ...(c.line ? { chargeLine: c.line } : {}) };

/** Step the local stack back one and restore what it held. A snapshot that will not parse is logged, not undone. */
function undoLocal(ctx: LocalContext): void {
  const h = ctx.historyRef.current;
  if (h.pointer <= 0) return;
  let snap;
  try {
    snap = JSON.parse(h.stack[h.pointer - 1]);
  } catch (e) {
    console.error('Undo: a snapshot that will not parse', e);
    return;
  }
  h.pointer--;
  ctx.restoreStates(snap.states ?? {}, snap.narrativeLogCounts);
  ctx.setActiveTab(snap.activeTab);
  ctx.setPendingAttack(null);
  if (snap.turnTracker && ctx.setTurnTrackerState) ctx.setTurnTrackerState(snap.turnTracker);
  if (snap.direction) ctx.imposeFrame?.(snap.direction);
  if (snap.cohesion !== undefined) ctx.imposeCohesion?.(snap.cohesion);
  if (snap.tokens) ctx.restoreTokens?.(snap.tokens);
  if (snap.chargeLine) ctx.dropLine?.(snap.chargeLine);
}

// ── The hook ─────────────────────────────────────────────────

/** Every ref the hook owns, kept across renders. */
function useHistoryRefs() {
  return {
    historyRef: useRef<LocalHistoryState>({ stack: [], pointer: -1, skip: false }),
    historyTrack: useRef<boolean>(false),
    undoingRef: useRef<boolean>(false),
    preActionSnapshotRef: useRef<HistorySnapshot | null>(null),
    historyPushTimerRef: useRef<ReturnType<typeof setTimeout> | null>(null),
    historyPushAccRef: useRef<HistorySnapshot[]>([]),
    /** How many renders we have waited for a late post-state. See historyShared's `holdBaseline`. */
    heldRetriesRef: useRef<number>(0),
    /** An action's entry is queued or being written: Undo waits (historyShared `queuePush`). */
    savingRef: useRef<boolean>(false),
    pushingRef: useRef<boolean>(false),
    savingFallbackRef: useRef<ReturnType<typeof setTimeout> | null>(null),
  };
}

/** Whether an action's entry is still being written, as state (for the button) and as a ref (for a Ctrl+Z). */
function useSaving(savingRef: MutableRefObject<boolean>) {
  const [saving, setState] = useState(false);
  const setSaving = useCallback(
    (on: boolean) => {
      savingRef.current = on;
      setState(on);
    },
    [savingRef],
  );
  return { saving, setSaving };
}

/** This render's contexts: one object carrying the params, the owned refs and the (null-safe) sync refs. */
function contextFor(
  params: UseHistoryParams,
  refs: ReturnType<typeof useHistoryRefs>,
  setSaving: (saving: boolean) => void,
) {
  const { syncRefs, ...rest } = params;
  return {
    ...rest,
    ...refs,
    setSaving,
    sessionId: params.sessionId,
    setSharedHistoryState: syncRefs?.setSharedHistoryState,
    sharedHistoryCacheRef: syncRefs?.sharedHistoryCacheRef,
    undoRedoBusyRef: syncRefs?.undoRedoBusyRef,
    narrativeQueueRef: syncRefs?.narrativeQueueRef,
    narrativeFlushTimerRef: syncRefs?.narrativeFlushTimerRef,
  };
}

type HistoryContext = ReturnType<typeof contextFor>;

/** Multiplayer: snapshot the table before an action. Never while an undo is restoring. */
function capturePreAction(ctx: HistoryContext): void {
  if (ctx.sessionId && !ctx.undoingRef.current) {
    const snap = snapshotOf(ctx.roster, ctx.turnTrackerState, Date.now() - 1, true, ctx.frame, ctx.cohesion);
    const origin = takeOrigin(ctx);
    if (origin) snap.undoTokens = origin.tokens;
    if (origin?.line) snap.chargeLine = origin.line;
    ctx.preActionSnapshotRef.current = snap;
    beginAction({ ...ctx, sessionId: ctx.sessionId });
  }
}

/**
 * After a tracked action re-renders, record its post-state - on the
 * shared stack in a session, on the local one solo. Suppressed while an
 * undo is restoring.
 */
function recordTrackedAction(ctx: HistoryContext): void {
  if (!ctx.historyTrack.current) return;
  ctx.historyTrack.current = false;
  if (ctx.undoingRef.current) return;
  const { sessionId } = ctx;
  if (sessionId) recordSharedAction({ ...ctx, sessionId });
  else pushLocal(ctx);
}

/**
 * Forget everything, locally and on the server.
 *
 * A scene cut's only caller (v8.337.0): a snapshot taken on one stage
 * and restored onto another would put the other scene's wounds and
 * order on this one.
 */
function resetAll(ctx: HistoryContext): void {
  ctx.historyRef.current = { stack: [], pointer: -1, skip: false };
  resetSharedHistory(ctx);
}

/** Undo one step - the shared stack in a session, the local one solo. */
async function undo(ctx: HistoryContext): Promise<void> {
  if (ctx.isLocked('undo')) return;
  const { sessionId } = ctx;
  if (sessionId) await undoShared({ ...ctx, sessionId });
  else undoLocal(ctx);
}

/** Undo for the table - see the file header. The refs it returns are the tracker's and the sync's handles. */
export default function useHistory(params: UseHistoryParams): UseHistoryReturn {
  const refs = useHistoryRefs();
  const { saving, setSaving } = useSaving(refs.savingRef);
  const ctx = contextFor(params, refs, setSaving);
  const sharedHistoryState = params.syncRefs?.sharedHistoryState ?? { canUndo: false };

  // Solo: snapshot on initial load. Once, on mount - deliberately no deps.
  useEffect(
    () => {
      if (!ctx.sessionId) pushLocal(ctx);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Push the post-mutation state after tracked actions re-render. Every render, by design.
  useEffect(() => recordTrackedAction(ctx));

  return {
    pushHistory: () => pushLocal(ctx),
    capturePreActionSnapshot: () => capturePreAction(ctx),
    doUndo: () => undo(ctx),
    // In a session, Undo waits while the newest action's entry is written (2026-10-24).
    canUndo: ctx.sessionId ? sharedHistoryState.canUndo && !saving : refs.historyRef.current.pointer > 0,
    saving: !!ctx.sessionId && saving,
    historyTrack: refs.historyTrack,
    undoingRef: refs.undoingRef,
    resetHistory: () => resetAll(ctx),
  };
}
